/* istanbul ignore file */
/* eslint-disable eol-last */
/**
 * Created by gboue on 8/24/22
 * File: OwnersTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useMemo, useContext } from 'react'
import {
  useModal,
} from '@panwds/react-ui'

import {
  ColumnOptions,
  ConnectedPaginator,
  ConnectedResultsSummary,
  ConnectedTable,
  TableProvider,
} from '@panwds/react-table'

import {
  LABEL_OWNERS_TABLE_HEADER_NAME,
  LABEL_OWNERS_TABLE_HEADER_TYPE,
  LABEL_OWNERS_TABLE_HEADER_STATUS,
  LABEL_OWNERS_TABLE_HEADER_ACTIONS,
  LABEL_OWNERS_ACTION_VERIFY,
  LABEL_OWNERS_ACTION_VERIFY_INSTRUCTIONS,
  LABEL_OWNERS_ACTION_DELETE,
} from '../../../../constants/LabelConstants'
import styled from 'styled-components'
import OwnerNameRenderer from './cellRenderer/OwnerNameRenderer'
import DomainVerificationTypeRenderer from './cellRenderer/DomainVerificationTypeRenderer'
import StatusRenderer from './cellRenderer/StatusRenderer'
import ActionsRenderer from './cellRenderer/ActionsRenderer'
import DomainModal, { SIDEBAR_OPTIONS } from '../../modal/DomainModal'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import DeleteOwnerModal from '../../modal/DeleteOwnerModal'
import AuthContext from '../../../../../common/context/AuthContext'
import { StringCompare } from '../../../../utils/string'

const CONTAINER = styled(V5TableWrapper)`
  flex-grow: 1;
  min-height: 10rem;
   display: flex;
   flex-direction: column;
   .tw-flex.tw-flex-col.tw-flex-grow.tw-overflow-hidden {
     & > .tw-flex-grow {
       min-height: 0 !important;
       max-height: 289px;
       overflow: auto;
     }
   }

   button[id^=table] {
     color: #333333;
   }

   button[aria-label="Previous"] {
     color: #333333;
   }

   button[aria-label="Next"] {
     color: #333333;
   }

  div[role="row"] {
    width: auto !important;
  }
  [role="table"] {
    flex-grow: 1;
  }
 `

/**
   *   Owners table list
   */
const OwnersTable: VFC<{
  federations?: any,
  email?: string,
  isVerified?: boolean,
  loading: boolean,
  owners?: any[],
  domain?: string,
  onHandleVerify: Function,
  fetchData: Function,
  verificationString?: string,
  setTxtError: Function,
  isVerifyLoading: any
}> = ({ email, isVerified, loading, owners, domain, onHandleVerify, fetchData, verificationString, federations, setTxtError, isVerifyLoading }) => {

  const { user } = useContext(AuthContext)
  const domainModal: { openModal: Function } = useModal(DomainModal)
  const deleteModal: { openModal: Function, closeModal: Function } = useModal(DeleteOwnerModal)

  const onActionClick = useCallback((action, owner) => {
    switch (action) {
      case LABEL_OWNERS_ACTION_VERIFY:
        onHandleVerify()
        break
      case LABEL_OWNERS_ACTION_VERIFY_INSTRUCTIONS:
        domainModal.openModal({
          federations,
          savedDomain: domain,
          fetchData,
          step: SIDEBAR_OPTIONS.VERIFY,
          verificationString,
          shouldFetch: false
        })
        break
      case LABEL_OWNERS_ACTION_DELETE:
        deleteModal.openModal({
          setTxtError,
          ownerName: email,
          domain,
          fetchData,
          type: owner.verification.type,
        })
        break
      default:
       // do nothing
    }
  }, [onHandleVerify, domainModal, fetchData, verificationString, deleteModal, domain, email, federations, setTxtError])

  const columns = useMemo(() => [
    {
      accessor: (row: any) => row?.user,
      Header: LABEL_OWNERS_TABLE_HEADER_NAME,
      Cell: ({ row, value }: any) => {
        return <OwnerNameRenderer{...{ row, value }} onClick={() => {}}/>
      },
      width: 232,
      id: 'owner_name',
    },
    {
      accessor: (row: any) => row?.verification?.type,
      Header: LABEL_OWNERS_TABLE_HEADER_TYPE,
      Cell: DomainVerificationTypeRenderer,
      width: 232,
      id: 'type',
    },
    {
      accessor: (row: any) => row?.verification?.status,
      Header: LABEL_OWNERS_TABLE_HEADER_STATUS,
      Cell: StatusRenderer,
      width: 232,
      id: 'status',
    },
    {
      accessor: (row: any) => row?.verification,
      Header: LABEL_OWNERS_TABLE_HEADER_ACTIONS,
      Cell: (props: any) => (
        <ActionsRenderer
          {...{ ...props, onActionClick, domain, fetchData, loading, isVerifyLoading }}
          openDeleteModal={deleteModal.openModal}
          closeDeleteModal={deleteModal.closeModal}
        />
      ),
      id: 'actions',
      sticky: true,
      stickyPosition: 'right'
    },
  ], [loading, isVerifyLoading, onActionClick, domain, fetchData, deleteModal.openModal, deleteModal.closeModal]) as ColumnOptions<any>[]

  const data = (owners || []).sort((ownerA, ownerB) => {
    if (ownerA.user === user.email) {
      return -1
    }
    if (ownerB.user === user.email) {
      return 1
    }
    return (StringCompare.Ascend(ownerA.user, ownerB.user) as number)
  })

  return (
    <CONTAINER>
      <TableProvider
        columns={columns}
        data={data}
        enablePagination
      >
        <div className='cm-flex cm-flex-col tw-flex-grow tw-overflow-auto'>
          <ConnectedTable/>
        </div>
        <div className='tw-flex tw-justify-between tw-items-center tw-p-1'>
          <ConnectedResultsSummary/>
          <ConnectedPaginator/>
        </div>
      </TableProvider>
    </CONTAINER>
  )
}

OwnersTable.propTypes = {}
export default OwnersTable
